import React, { useEffect } from "react";
import mondaySdk from "monday-sdk-js";
import { useSelector } from "react-redux";
import "@vibe/core/tokens";
import useUser from "../hooks/context/userHook";
import { useCheckUserOnboarding } from "../hooks/api/useCheckUserOnboarding";
import PageLoader from "../components/PageLoader";
import ErrorPage from "../components/errorPage";
import BoardsDialogContent from "../components/BoardsDialog/BoardsDialogContent";
import { RootState } from "../redux/store/store";

const monday = mondaySdk();

/**
 * The on-click dialog of SIMB's dropdown-column app feature (monday
 * `AppFeatureDialog`). monday opens this route in a small popover anchored to
 * the clicked cell, with `itemId`/`boardId`/`columnId` in the context — the
 * monday slice already maps those to `itemIds`/`currentBoard`.
 *
 * This page is only the gate. Like every other SIMB surface it defers to
 * `useCheckUserOnboarding`, which sends a user who has not authorised the app
 * to `/onboarding`; the OAuth round-trip then lands on `/` and `home.tsx` hops
 * back here through `pendingAuthRedirect` (see APP_FEATURE_ROUTES). The content
 * is mounted only once the user is known to be authorised, so the item
 * locations request never races the onboarding check.
 */
const BoardsDialog = () => {
  const { userId } = useUser();
  const isViewOnly = useSelector((state: RootState) => state.monday.isViewOnly);
  const { isAuthorized, isLoading, error } = useCheckUserOnboarding(userId);

  useEffect(() => {
    if (isAuthorized) {
      monday.execute("valueCreatedForUser");
    }
  }, [isAuthorized]);

  if (isViewOnly) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="px-4 py-3 bg-red-100 rounded">
          <span className="block sm:inline">As a viewer, you are unable to use the SIMB.</span>
        </div>
      </div>
    );
  }

  if (error) {
    return <ErrorPage />;
  }

  if (isLoading || !isAuthorized) {
    return <PageLoader />;
  }

  return <BoardsDialogContent />;
};

export default BoardsDialog;
